import CardCurrency from "@/components/shared/CardCurrency/CardCurrency";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import useModalStore from "@/store/useModalStore";
import { useAuth } from "@/auth/Auth";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface WalletCardsProps {
  available: string;
  retention: string;
  anticipation: string;
  totalWithdrawn: string;
  isLoading: boolean;
  canViewAvailable?: boolean;
  canViewToRelease?: boolean;
  canViewRetention?: boolean;
  canViewTotalWithdrawn?: boolean;
  canRequestWithdrawal?: boolean;
  canRequestAnticipation?: boolean;
}

export default function WalletCards({
  available,
  retention,
  totalWithdrawn,
  anticipation,
  isLoading,
  canViewAvailable = true,
  canViewToRelease = true,
  canViewRetention = true,
  canViewTotalWithdrawn = true,
  canRequestWithdrawal = false,
  canRequestAnticipation = false,
}: WalletCardsProps) {
  const { t } = useTranslation(['wallet', 'common']);
  const { openModal } = useModalStore();
  const { authData } = useAuth();

  return (
    <div className='grid lg:grid-cols-2 gap-4 max-xl:grid-cols-1'>
      {canViewAvailable && (
        <CardCurrency
          text={t('available')}
          value={available}
          status='paid'
          button={
            canRequestWithdrawal ? (
              Number(available?.replace(/[^\d-]/g, '').replace(',', '.')) < 5 ? (
                <Tooltip>
                  <TooltipTrigger className='w-full'>
                    <Button
                      data-cy='abrir-modal-saque'
                      disabled
                      className='max-xl:w-full'
                    >
                      {t('requestWithdrawal')}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('wallet:amount_greater_than_five')}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  data-cy='abrir-modal-saque'
                  onClick={() => {
                    authData?.documentation_status === 'pending' ||
                    authData?.documentation_status === 'disapproved'
                      ? openModal('document_pending')
                      : openModal('withdrawal-form');
                  }}
                  className='max-xl:w-full'
                >
                  {t('requestWithdrawal')}
                </Button>
              )
            ) : undefined
          }
          isLoading={isLoading}
        />
      )}

      {canViewToRelease && (
        <CardCurrency
          text={t('dashboard:toRelease')}
          value={anticipation}
          status='pending'
          button={
            canRequestAnticipation ? (
              <Tooltip>
                <TooltipTrigger className='w-full'>
                  <Button
                    variant='secondary'
                    data-cy='abrir-modal-saque'
                    disabled
                    onClick={() => {
                      authData?.documentation_status === 'pending' ||
                      authData?.documentation_status === 'disapproved'
                        ? openModal('document_pending')
                        : openModal('withdrawal-form');
                    }}
                    className='max-xl:w-full'
                  >
                    {t('requestAdditionalWithdrawal')}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('common:coming_soon')}</TooltipContent>
              </Tooltip>
            ) : undefined
          }
          isLoading={isLoading}
        />
      )}

      {canViewRetention && (
        <CardCurrency
          text={t('inRetention')}
          value={retention}
          status='refused'
          isLoading={isLoading}
        />
      )}
      {canViewTotalWithdrawn && (
        <CardCurrency
          text={t('totalWithdrawn')}
          value={totalWithdrawn}
          status='period'
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
